import { Loader2 } from "lucide-react";

import { useMySupportRequests } from "@/hooks/support";
import { cn } from "@/lib/utils";
import type { MySupportRequest, SupportRequestStatus, SupportRequestType } from "@/types";

const TYPE_LABELS: Record<SupportRequestType, string> = {
  question: "Question",
  feature_request: "Feature request",
  other: "Other",
};

const STATUS_LABELS: Record<SupportRequestStatus, string> = {
  new: "New",
  in_progress: "In progress",
  resolved: "Resolved",
};

const STATUS_CLASSES: Record<SupportRequestStatus, string> = {
  new: "bg-[#EAF2FB] text-[#2563EB]",
  in_progress: "bg-[#FBF4E9] text-[#B0793C]",
  resolved: "bg-[#E9F1E6] text-[#1E6F4F]",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function RequestCard({ request }: { request: MySupportRequest }) {
  return (
    <li className="rounded-[11px] border border-content-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium text-sm text-content-foreground">{request.subject}</p>
          <p className="mt-0.5 text-xs text-content-muted">
            {TYPE_LABELS[request.type]} · {formatDate(request.created_at)}
          </p>
        </div>
        <span
          className={cn(
            "flex-none rounded-full px-2.5 py-1 text-[11px] font-medium",
            STATUS_CLASSES[request.status]
          )}
        >
          {STATUS_LABELS[request.status]}
        </span>
      </div>

      {request.reply_body ? (
        <div className="mt-3 rounded-[9px] bg-[#F0F8F2] p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#1E6F4F]">
            Our reply{request.replied_at ? ` · ${formatDate(request.replied_at)}` : ""}
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-content-foreground">
            {request.reply_body}
          </p>
        </div>
      ) : (
        <p className="mt-3 text-xs text-content-muted">
          We've received this and will follow up by email.
        </p>
      )}
    </li>
  );
}

/**
 * Read-only list of the current user's support requests and any admin Reply.
 * Extracted from SupportPage so it can be tested without the dashboard chrome.
 * Never renders internal admin notes (they aren't in the payload).
 */
export function MyRequests() {
  const { data, isPending, isError, refetch } = useMySupportRequests();

  return (
    <section className="rounded-[14px] border border-content-border bg-card px-7 py-[26px]">
      <h2 className="text-xl font-bold tracking-[-.01em] text-card-foreground mb-1">
        Your requests
      </h2>
      <p className="text-sm text-content-muted mb-5">
        Questions and feedback you've submitted, and our replies.
      </p>

      {isPending ? (
        <div className="flex items-center gap-2 py-6 text-sm text-content-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your requests…
        </div>
      ) : isError ? (
        <div className="flex items-center justify-between gap-3 rounded-[9px] border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <span>Couldn't load your requests.</span>
          <button type="button" onClick={() => refetch()} className="font-medium hover:underline">
            Try again
          </button>
        </div>
      ) : data && data.length > 0 ? (
        <ul className="space-y-3">
          {data.map((request) => (
            <RequestCard key={request.id} request={request} />
          ))}
        </ul>
      ) : (
        <p className="py-6 text-sm text-content-muted">You haven't submitted any requests yet.</p>
      )}
    </section>
  );
}
